import { createFileRoute } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ChevronsUpDown, Pencil, Plus } from 'lucide-react';
import { ChangeEvent, Suspense, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button/button';
import { GenericTable } from '@/components/ui/generic-table';
import { Input } from '@/components/ui/input';
import { ViewContent, ViewHeader } from '@/components/view-layout';
import { useAllManagedSuppliers } from '@/features/suppliers/api/supplier-queries';
import { AddManagedSupplierDialog } from '@/features/suppliers/components/add-managed-supplier-dialog';
import { EditSupplierDialog } from '@/features/suppliers/components/edit-supplier-dialog';
import { Supplier } from '@/features/suppliers/types/supplier-types';

import { Route as AdminSupplierIngredientsRoute } from './$supplierId/index';

const columnHelper = createColumnHelper<Supplier>();

const makeColumns = (onEdit: (supplier: Supplier) => void) => [
  columnHelper.accessor('title', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Name
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => (
      <div className="flex items-center gap-2">{info.getValue()}</div>
    ),
    size: 200,
  }),
  columnHelper.accessor('ingredients', {
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
          className="w-[110px] px-0 justify-start"
        >
          Ingredients
          <ChevronsUpDown className="ml-2 h-4 w-4" />
        </Button>
      );
    },
    cell: (info) => info.getValue(),
    size: 100,
  }),
  columnHelper.accessor('created_at', {
    header: 'Created',
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    size: 120,
  }),
  columnHelper.accessor('updated_at', {
    header: 'Updated',
    cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    size: 120,
  }),
  columnHelper.display({
    id: 'actions',
    header: () => <div className="w-[60px]"></div>,
    cell: (info) => (
      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(info.row.original);
          }}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      </div>
    ),
    size: 60,
  }),
];

interface SuppliersTableProps {
  searchQuery: string;
  onEdit: (supplier: Supplier) => void;
}

export function SuppliersTable({ searchQuery, onEdit }: SuppliersTableProps) {
  const { data: suppliers } = useAllManagedSuppliers();
  const navigate = Route.useNavigate();

  const columns = useMemo(() => makeColumns(onEdit), [onEdit]);

  const table = useReactTable({
    data: suppliers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  useEffect(() => {
    table.setGlobalFilter(searchQuery);
  }, [searchQuery, table]);

  const handleRowClick = async (supplier: Supplier) => {
    // Navigate to supplier ingredients page
    await navigate({
      to: AdminSupplierIngredientsRoute.to,
      params: {
        supplierId: supplier.id,
      },
    });
  };

  return (
    <GenericTable
      table={table}
      skeletonRows={5}
      onClick={handleRowClick}
      emptyMessage="No managed suppliers found."
    />
  );
}

export default function AdminSuppliersPage() {
  const { search } = Route.useSearch();
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  const navigate = Route.useNavigate();

  const columns = useMemo(
    () =>
      makeColumns(() => {
        // This is just for the fallback table, the actual onEdit is passed to SuppliersTable
      }),
    [],
  );

  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const handleSearchChange = async (e: ChangeEvent<HTMLInputElement>) => {
    await navigate({
      search: {
        search: e.target.value,
      },
    });
  };

  const handleAddSupplier = () => {
    setShowAddDialog(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setEditingSupplier(supplier);
  };

  return (
    <>
      <ViewHeader title="Managed Suppliers">
        <div className="flex gap-4 items-center">
          <Input
            placeholder="Search suppliers..."
            value={search}
            onChange={handleSearchChange}
          />
          <Button onClick={handleAddSupplier}>
            <Plus className="h-4 w-4 mr-2" />
            Add Supplier
          </Button>
        </div>
      </ViewHeader>
      <ViewContent>
        <Suspense
          fallback={
            <GenericTable table={table} loading={true} skeletonRows={5} />
          }
        >
          <SuppliersTable searchQuery={search} onEdit={handleEditSupplier} />
        </Suspense>
      </ViewContent>
      <AddManagedSupplierDialog
        open={showAddDialog}
        onOpenChange={setShowAddDialog}
      />
      <EditSupplierDialog
        supplier={editingSupplier}
        onClose={() => setEditingSupplier(null)}
      />
    </>
  );
}

const adminSuppliersPageSearchSchema = z.object({
  search: z.string().default('').catch(''),
});

export const Route = createFileRoute('/_authenticated/admin/suppliers/')({
  validateSearch: adminSuppliersPageSearchSchema,
  component: AdminSuppliersPage,
});
